/**
 * Top-level ErrorBoundary (OCP-79). Renders a graceful fallback when
 * a React render path throws + forwards the error to Sentry via the
 * SDK's React integration. Operator sees enough to triage (event id
 * to paste into Sentry search) + a "reload" affordance.
 */
import type { ReactNode } from 'react';

import { SentryErrorBoundary } from '../lib/sentry';

interface FallbackProps {
  error: unknown;
  eventId: string;
  resetError: () => void;
}

function Fallback({ error, eventId, resetError }: FallbackProps) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div
      className="mx-auto mt-10 max-w-md rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-gray-900 p-6 shadow-sm"
      data-testid="app-error-boundary-fallback"
    >
      <h1 className="text-lg font-semibold text-red-800 dark:text-red-300">Something broke</h1>
      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
        The operator team has been notified. You can try reloading the page —
        if it keeps happening, share the event id with the team.
      </p>
      <div className="mt-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
        event: {eventId || '(not captured — Sentry disabled)'}
      </div>
      <p className="mt-2 truncate text-xs text-gray-500" title={message}>
        {message}
      </p>
      <button
        type="button"
        onClick={resetError}
        className="mt-4 rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Try again
      </button>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <SentryErrorBoundary
      fallback={(props) => (
        <Fallback error={props.error} eventId={props.eventId} resetError={props.resetError} />
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
}
